import React, { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate
} from "react-router-dom";
import {
  NOTIFICATION_APPS,
  fetchWhatsAppAppStatus
} from "../../api/notifications/notificationAppsApi";
import {
  useToast
} from "../../context/ToastContext";

const statusStyles = {
  CONNECTED: "bg-green-100 text-green-800 border-green-200",
  CONNECTING: "bg-amber-100 text-amber-800 border-amber-200",
  DISCONNECTED: "bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800",
  COMING_SOON: "bg-slate-100 dark:bg-neutral-900 text-neutral-500 dark:text-neutral-400 border-neutral-200 dark:border-neutral-800",
};

const NotificationAppsHub = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const [waStatus, setWaStatus] = useState("DISCONNECTED");
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);

  const toast = useToast();

  const refresh = useCallback(async () => {
    try {
      const res = await fetchWhatsAppAppStatus(isPortal);
      setWaStatus(res.status || "DISCONNECTED");
      if (!res.success && res.error) toast.error(res.error);
      setLastChecked(new Date());
    } catch (err) {
      setWaStatus("DISCONNECTED");
      toast.error(err.message || "Failed to check status");
    } finally {
      setLoading(false);
    }
  }, [isPortal]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  }, [refresh]);

  const statusFor = (app) => {
    if (!app.functional) return "COMING_SOON";
    if (app.channel === "WHATSAPP") return waStatus;
    return "DISCONNECTED";
  };

  const onPrimary = (app) => {
    if (!app.functional) return;
    if (app.channel === "WHATSAPP") {
      const path = isPortal ? app.connectPathPortal : app.connectPath;
      if (path) navigate(path);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Notification apps</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Connect external channels used to send fee and school notifications.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            refresh();
          }}
          className="text-sm px-3 py-1.5 border rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800"
        >
          Refresh status
        </button>
      </div>

      

      {lastChecked && (
        <p className="text-xs text-neutral-400 dark:text-neutral-500">
          Last checked: {lastChecked.toLocaleTimeString()}
          {loading ? " · updating…" : ""}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {NOTIFICATION_APPS.map((app) => {
          const st = statusFor(app);
          const connected = st === "CONNECTED";
          const btnLabel = !app.functional
            ? "Coming soon"
            : connected
              ? "Manage / Disconnect"
              : st === "CONNECTING"
                ? "Finish connecting"
                : "Connect";

          return (
            <div
              key={app.id}
              className="bg-white dark:bg-neutral-900 border rounded-xl p-5 shadow-sm flex flex-col"
            >
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-semibold text-neutral-900 dark:text-neutral-100">{app.name}</h2>
                <span
                  className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
                    statusStyles[st] || statusStyles.DISCONNECTED
                  }`}
                >
                  {st === "COMING_SOON" ? "Coming soon" : st}
                </span>
              </div>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 flex-1">{app.description}</p>

              {app.channel === "WHATSAPP" && (
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-2">
                  Live status from WhatsApp session (same as pairing page).
                </p>
              )}

              <button
                type="button"
                disabled={!app.functional}
                onClick={() => onPrimary(app)}
                className={`mt-4 w-full py-2 rounded-lg text-sm font-medium transition ${ !app.functional ? "bg-neutral-100 dark:bg-neutral-800 dark:bg-neutral-900 text-neutral-400 dark:text-neutral-500 cursor-not-allowed" : connected ? "bg-gray-800 text-white hover:bg-gray-900" : "bg-emerald-600 text-white hover:bg-emerald-700" }`}
              >
                {btnLabel}
              </button>

              {app.functional && app.connectPath && (
                <Link
                  to={isPortal ? app.connectPathPortal : app.connectPath}
                  className="mt-2 text-center text-xs text-blue-600 hover:underline"
                >
                  Open {app.name} setup →
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-sm text-neutral-500 dark:text-neutral-400 border rounded-lg p-4 bg-neutral-50 dark:bg-neutral-950">
        <p className="font-medium text-neutral-700 dark:text-neutral-200">How send works</p>
        <ul className="list-disc ml-5 mt-1 space-y-1">
          <li>Connect WhatsApp here (pairing page), then use send icons on fee / student screens.</li>
          <li>SMS and Email will appear the same way once their connectors are added.</li>
          <li>Send always checks live connection status before queueing WhatsApp messages.</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationAppsHub;
